import type { PackPurchase, PackPurchaseBatch } from "@/api";

/**
 * Quick Rip recovery state, scoped PER PLAYER.
 *
 * Two failure modes drove this design (2026-08-06 founder report):
 * 1. A global intent key made every "Open pack" click re-POST the same
 *    clientRef, so the backend idempotently replayed the first delivered
 *    batch forever instead of selling a second pack.
 * 2. A global queue key restored the last delivered reveal on every Packs
 *    visit — including for a DIFFERENT account in the same browser.
 *
 * Rules encoded here:
 * - Storage keys carry the player id; another account's state is discarded.
 * - The queue is only restorable while a reveal is genuinely unfinished.
 *   Closing the overlay or finishing the last summary consumes it.
 * - The intent (idempotency key) survives only while a purchase is still
 *   reconciling (202). Delivery clears it; new purchases mint a new key.
 */

export const QUEUE_PREFIX = "hw.quick-rip.queue.v3";
export const INTENT_PREFIX = "hw.quick-rip.intent.v2";

const LEGACY_KEYS = [
  "hw.quick-rip.queue.v1",
  "hw.quick-rip.queue.v2",
  "hw.quick-rip.intent.v1",
];

export type SavedQueue = {
  purchases: PackPurchase[];
  index: number;
  instant: boolean;
};

export type PurchaseIntent = {
  intent: string;
  clientRef: string;
  code: string;
  quantity: number;
  instant: boolean;
};

export function queueKey(playerId: number) {
  return `${QUEUE_PREFIX}:${playerId}`;
}

export function intentKey(playerId: number) {
  return `${INTENT_PREFIX}:${playerId}`;
}

export function isDeliveredPurchase(purchase: PackPurchase) {
  return (
    purchase.status === "delivered" &&
    purchase.contents.length > 0 &&
    purchase.deliveredAt !== null
  );
}

export function isDeliveredBatch(batch: PackPurchaseBatch) {
  return (
    batch.status === "delivered" &&
    batch.purchases.length === batch.count &&
    batch.purchases.every(isDeliveredPurchase)
  );
}

/** Remove legacy global keys and any state belonging to OTHER players. */
export function clearForeignRecoveryState(playerId: number) {
  const mine = new Set([queueKey(playerId), intentKey(playerId)]);
  const stale: string[] = [];
  for (let index = 0; index < window.sessionStorage.length; index += 1) {
    const key = window.sessionStorage.key(index);
    if (!key) continue;
    if (LEGACY_KEYS.includes(key)) {
      stale.push(key);
      continue;
    }
    if (
      (key.startsWith(`${QUEUE_PREFIX}:`) ||
        key.startsWith(`${INTENT_PREFIX}:`)) &&
      !mine.has(key)
    ) {
      stale.push(key);
    }
  }
  for (const key of stale) window.sessionStorage.removeItem(key);
}

export function loadQueue(playerId: number): SavedQueue | null {
  try {
    const queue = JSON.parse(
      window.sessionStorage.getItem(queueKey(playerId)) ?? "null",
    ) as SavedQueue | null;
    if (
      !queue ||
      !Array.isArray(queue.purchases) ||
      !queue.purchases.length ||
      !queue.purchases.every(isDeliveredPurchase) ||
      !Number.isInteger(queue.index) ||
      queue.index < 0 ||
      queue.index >= queue.purchases.length
    ) {
      window.sessionStorage.removeItem(queueKey(playerId));
      return null;
    }
    return queue;
  } catch {
    window.sessionStorage.removeItem(queueKey(playerId));
    return null;
  }
}

export function saveQueue(playerId: number, queue: SavedQueue) {
  window.sessionStorage.setItem(queueKey(playerId), JSON.stringify(queue));
}

export function clearQueue(playerId: number) {
  window.sessionStorage.removeItem(queueKey(playerId));
}

export function loadIntent(playerId: number): PurchaseIntent | null {
  try {
    const intent = JSON.parse(
      window.sessionStorage.getItem(intentKey(playerId)) ?? "null",
    ) as PurchaseIntent | null;
    if (
      !intent?.clientRef ||
      !intent.code ||
      !Number.isInteger(intent.quantity)
    ) {
      window.sessionStorage.removeItem(intentKey(playerId));
      return null;
    }
    return intent;
  } catch {
    window.sessionStorage.removeItem(intentKey(playerId));
    return null;
  }
}

export function saveIntent(playerId: number, intent: PurchaseIntent) {
  window.sessionStorage.setItem(intentKey(playerId), JSON.stringify(intent));
}

export function clearIntent(playerId: number) {
  window.sessionStorage.removeItem(intentKey(playerId));
}
